import { z, ZodType } from 'zod';

import { typenames } from '~/components/organisms/slices/content-blocks/@types/typenames';
import { galleryMediaSchema } from '~/lib/api/fragments/gallery-media';
import { imageSchema } from '~/lib/api/fragments/image';
import { linkSchema } from '~/lib/api/fragments/link';
import {
  mediaSchema,
  optionalBuiltinMediaSchema,
  optionalMediaSchema,
} from '~/lib/api/fragments/media';
import { postObjectSchema } from '~/lib/api/fragments/post-object';

const schemasTuple = [
  /* cmsHeaderLayout */
  z.object({
    __typename: z.literal(typenames.cmsHeaderLayout),
    mainTitle: z.string().nullish(),
    secondaryTitle: z.string().nullish(),
    backgroundImage: imageSchema.nullish(),
  }),

  /* homepageHeaderLayout */
  z.object({
    __typename: z.literal(typenames.homepageHeaderLayout),
    mainMedia: optionalMediaSchema.nullish(),
    secondaryMedia: optionalMediaSchema.nullish(),
    tagline: z.string().nullish(),
    secondaryTitle: z.string().nullish(),
  }),

  /* instagramFeedLayout */
  z.object({
    __typename: z.literal(typenames.instagramFeedLayout),
    title: z.string().nullish(),
    feedId: z.coerce.number(),
    link: linkSchema,
  }),

  /* largeTextLayout */
  z.object({
    __typename: z.literal(typenames.largeTextLayout),
    size: z.enum(['auto', 'full_height']),
    position: z.enum(['left', 'center', 'right']),
    tagType: z.enum(['text', 'h1', 'h2', 'h3', 'h4']),
    textContent: z.string().nullish(),
  }),

  /* mondrianLayout */
  z.object({
    __typename: z.literal(typenames.mondrianLayout),
    lines: z.array(
      z.object({
        blocks: z.array(
          z
            .object({
              flexFactor: z.coerce.number(),
            })
            .and(
              z.discriminatedUnion('blockType', [
                z.object({
                  blockType: z.literal('media'),
                  mediaContent: mediaSchema,
                }),

                z.object({
                  blockType: z.literal('internal_link'),
                  internalLinkContent: z.object({
                    postObject: z.object({
                      node: z
                        .object({
                          id: z.string(),
                          slug: z.string(),
                        })
                        .and(
                          z.discriminatedUnion('__typename', [
                            z.object({
                              __typename: z.literal('Page'),
                              title: z.string(),
                              content: z.string(),
                              featuredImage: imageSchema.nullish(),
                            }),

                            z.object({
                              __typename: z.literal('Post'),
                              title: z.string(),
                              excerpt: z.string(),
                              featuredImage: imageSchema.nullish(),
                              categories: z.object({
                                edges: z.array(
                                  z.object({
                                    node: z.object({
                                      id: z.string(),
                                      slug: z.string(),
                                    }),
                                  })
                                ),
                              }),
                            }),

                            z.object({
                              __typename: z.literal('Property'),
                              title: z.string(),
                              excerpt: z.string(),
                              featuredImage: imageSchema.nullish(),
                              propertyTypes: z.object({
                                edges: z.array(
                                  z.object({
                                    node: z.object({
                                      id: z.string(),
                                      slug: z.string(),
                                    }),
                                  })
                                ),
                              }),
                            }),
                          ])
                        ),
                    }),
                  }),
                }),
              ])
            )
        ),
      })
    ),
  }),

  /* imageAndTextLayout */
  z.object({
    __typename: z.literal(typenames.imageAndTextLayout),
    layout: z.enum(['image-text', 'text-image']),
    textContent: z.string().nullish(),
    ...mediaSchema.shape,
  }),

  /* imageBannerAndTitle */
  z.object({
    __typename: z.literal(typenames.imageBannerAndTitle),
    title: z.string().nullish(),
    ...mediaSchema.shape,
  }),

  /* tilesList */
  z.object({
    __typename: z.literal(typenames.tilesListLayout),
    title: z.string().nullish(),
    textContent: z.string().nullish(),
    tiles: z
      .array(
        z.object({
          title: z.string().nullish(),
          textContent: z.string().nullish(),
        })
      )
      .nullish(),
  }),

  /* teamLayout */
  z.object({
    __typename: z.literal(typenames.teamLayout),
    teamMembers: z
      .array(
        z.object({
          fullName: z.string(),
          jobTitle: z.string().nullish(),
          biography: z.string().nullish(),
          contactEmail: z.string().nullish(),
          linkedinLink: linkSchema.nullish(),
          image: imageSchema,
        })
      )
      .nullish(),
  }),

  /* threeColumns */
  z.object({
    __typename: z.literal(typenames.threeColumnsLayout),
    title: z.string().nullish(),
    columnLeft: z.object({
      title: z.string().nullish(),
      textContent: z.string().nullish(),
    }),
    columnCenter: z.object({
      title: z.string().nullish(),
      textContent: z.string().nullish(),
    }),
    columnRight: z.object({
      title: z.string().nullish(),
      textContent: z.string().nullish(),
    }),
  }),

  /* flexibleTextLayout */
  z.object({
    __typename: z.literal(typenames.flexibleTextLayout),
    textAlign: z.enum(['left', 'center', 'right']),
    columnWidth: z.enum(['narrow', 'wide']).nullish(),
    columns: z.array(
      z.object({
        title: z.string().nullish(),
        secondaryTitle: z.string().nullish(),
        content: z.string().nullish(),
      })
    ),
  }),

  /* aaltoSceneLayout */
  z.object({
    __typename: z.literal(typenames.aaltoSceneLayout),
    backgroundImage: imageSchema.nullish(),
    links: z.array(
      z.object({
        ...postObjectSchema.shape,
        primaryTitle: z.string().nullish(),
        secondaryTitle: z.string().nullish(),
        ...optionalBuiltinMediaSchema.shape,
      })
    ),
  }),

  /* wysiwygLayout */
  z.object({
    __typename: z.literal(typenames.wysiwygLayout),
    content: z.string(),
  }),

  /* contactFormLayout */
  z.object({
    __typename: z.literal(typenames.contactFormLayout),
    checkboxLabel: z.string().nullish(),
  }),

  /* imagesGalleryLayout */
  z.object({
    __typename: z.literal(typenames.imagesGalleryLayout),
    gallery: z.object({
      // edges: z.array(imageSchema),
      edges: z.array(galleryMediaSchema),
    }),
  }),

  /* singleMediaLayout */
  z.object({
    __typename: z.literal(typenames.singleMediaLayout),
    gridColumn: z.coerce.number(),
    gridSpan: z.coerce.number(),
    ...mediaSchema.shape,
  }),
] as const;

export const schemas = {
  cmsHeaderLayout: schemasTuple[0],
  homepageHeaderLayout: schemasTuple[1],
  instagramFeedLayout: schemasTuple[2],
  largeTextLayout: schemasTuple[3],
  mondrianLayout: schemasTuple[4],
  imageAndTextLayout: schemasTuple[5],
  imageBannerAndTitle: schemasTuple[6],
  tilesListLayout: schemasTuple[7],
  teamLayout: schemasTuple[8],
  threeColumnsLayout: schemasTuple[9],
  flexibleTextLayout: schemasTuple[10],
  aaltoSceneLayout: schemasTuple[11],
  wysiwygLayout: schemasTuple[12],
  contactFormLayout: schemasTuple[13],
  imagesGalleryLayout: schemasTuple[14],
  singleMediaLayout: schemasTuple[15],
} satisfies Record<keyof typeof typenames, ZodType>;

const sliceMachineSchema = z.object({
  slices: z.array(z.discriminatedUnion('__typename', schemasTuple)).nullish(),
});

export default sliceMachineSchema;
